import {isUndefined} from 'lodash';
import React, {FC, ReactElement, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {DropdownEmpty} from '../../dropdowns/dropdownEmpty';
import {BaseFilterDropdownProps} from '../common/types';

/*
 * Props.
 */

interface MoreFiltersDropdownProps {
  popoverProps: PopoverTransferProps;
  dropdownItems: ReadonlyArray<MoreFiltersDropdownItem>;
}

/*
 * Constants.
 */

const defaultMoreFiltersWidth = 180;

/*
 * Styles.
 */

const StyledDropdownEmpty = styled(DropdownEmpty)`
  padding: 20px;
`;

/*
 * Types.
 */

export type FilterDropdownRenderer = (props: BaseFilterDropdownProps) => ReactElement;

export interface MoreFiltersDropdownItem {
  renderDropdown: FilterDropdownRenderer;
  title: ReactNode;
  id: string;
  onItemSelect?: Handler;
}

/*
 * Component.
 */

export const MoreFiltersDropdown: FC<MoreFiltersDropdownProps> = ({popoverProps, dropdownItems}) => {
  const [selectedFilter, setSelectedFilter] = useState<MoreFiltersDropdownItem | undefined>(undefined);

  if (!isUndefined(selectedFilter)) {
    return selectedFilter.renderDropdown(popoverProps);
  }

  return (
    <SimpleDropdown {...popoverProps} width={defaultMoreFiltersWidth}>
      {dropdownItems.length > 0 ? (
        dropdownItems.map((dropdownItem) => (
          <SimpleDropdownItem
            key={dropdownItem.id}
            onSelect={(event) => {
              // Keep the dropdown open on item select, since we want to use the
              // same props to render a new dropdown above
              event.preventDefault();
              setSelectedFilter(dropdownItem);

              dropdownItem.onItemSelect?.();
            }}
          >
            {dropdownItem.title}
          </SimpleDropdownItem>
        ))
      ) : (
        <StyledDropdownEmpty>
          <Trans id="more-filters-no-filters-dropdown">No available filters</Trans>
        </StyledDropdownEmpty>
      )}
    </SimpleDropdown>
  );
};
